import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';
import pluralize from 'pluralize';

import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';

import { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import NewScheduleFormBase from '~/components/Common/Forms/NewScheduleFormBase';
import PermissionedComponent from '~/components/Common/PermissionedComponent';
import ScheduleTable, { ScheduleTableHandle } from '~/components/Offers/Edit/common/ScheduleTable';
import { useScheduleSection } from '~/components/Offers/Edit/common/useScheduleSection';
import { useUndoableDelete } from '~/components/Offers/Edit/common/useUndoableDelete';
import {
  formatListVisibilityDateConflictMessage,
  getListVisibilityDateConflict,
} from '~/components/Offers/Edit/common/utils';

import { ROLE_ADMIN_USER, ROLE_HOTEL_COORDINATOR } from '~/consts/roles';

import { CUSTOMER_FACING_TYPES } from './campaignScheduleBrandRows';
import type { CampaignScheduleBrandRow } from './types';

interface Props {
  offerId: string;
  open: boolean;
  onClose: () => void;
  row: CampaignScheduleBrandRow | undefined;
}

export default function CampaignScheduleBrandGroupModal({ offerId, open, onClose, row }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false);
  const { onSaveFinished, onUpdateErrors, onDelete, onError, invalidateSchedules } = useScheduleSection({
    offerId,
    shouldMigrateSchedule: false,
  });

  // Keeps rendering the last-known row while the Dialog plays its closing transition, so it
  // doesn't flash empty/crash if `row` disappears out from under an open modal — e.g. "Delete All"
  // removing the last schedules of a LuxPlus-only group, which has no other type to keep it alive
  // in brandRows. `isOpen` below (not `open`) is what actually drives visibility.
  const [lastRow, setLastRow] = useState(row);
  useEffect(() => {
    if (row) {
      setLastRow(row);
    }
  }, [row]);

  const displayRow = row ?? lastRow;
  const isOpen = open && !!row;

  const editableSchedules =
    displayRow?.schedules.filter((schedule) => CUSTOMER_FACING_TYPES.includes(schedule.type)) ?? [];
  const brandLabel = displayRow?.brand ?? '';
  const types = CUSTOMER_FACING_TYPES.filter((type) => editableSchedules.some((schedule) => schedule.type === type));

  const {
    confirm: executeDeleteAll,
    isDeleting: isDeletingAll,
    isUndoWindowOpen: isDeleteAllUndoWindowOpen,
    cancel: cancelDeleteAll,
  } = useUndoableDelete({
    offerId,
    label: `All ${editableSchedules.length} ${pluralize('schedule', editableSchedules.length)} for ${brandLabel}`,
    getIdList: () => editableSchedules.map((schedule) => schedule.id),
    onSuccess: invalidateSchedules,
    onError,
  });

  // Stages one start/end range into every row currently listed in this row's table — scoped to
  // just this row/brand-group, unlike the full-page bulk update which lets you add brand-new
  // regions from the whole brand's region list. Doesn't save on its own: it only populates each
  // row's pending (unsaved) value via ScheduleTable's own edit state, so the operator can still
  // review/tweak individual rows before clicking the existing Save button below the table.
  const scheduleTableRef = useRef<ScheduleTableHandle>(null);
  const [bulkStart, setBulkStart] = useState('');
  const [bulkEnd, setBulkEnd] = useState('');
  const [bulkUpdateError, setBulkUpdateError] = useState<string | null>(null);
  const [isTableSaving, setIsTableSaving] = useState(false);

  const onBulkReset = useCallback(() => {
    setBulkStart('');
    setBulkEnd('');
    setBulkUpdateError(null);
  }, []);

  useEffect(() => {
    onBulkReset();
  }, [displayRow?.key, onBulkReset]);

  if (!displayRow) {
    return null;
  }

  const brand = displayRow.schedules[0]?.brand as Brand | undefined;

  const confirmDeleteAll = () => {
    setIsDeleteAllOpen(false);
    executeDeleteAll();
  };

  const onBulkApply = () => {
    if (editableSchedules.length === 0 || !bulkStart || !bulkEnd) {
      return;
    }

    if (!displayRow.isLuxPlus) {
      const conflict = getListVisibilityDateConflict({ start: bulkStart, end: bulkEnd }, displayRow.schedules);
      if (conflict) {
        setBulkUpdateError(formatListVisibilityDateConflictMessage(conflict));
        return;
      }
    }

    setBulkUpdateError(null);
    scheduleTableRef.current?.applyBulkDates(bulkStart, bulkEnd);
    enqueueSnackbar(
      `Applied to ${editableSchedules.length} ${pluralize('schedule', editableSchedules.length)} — click Save below to confirm`,
      { variant: 'info' },
    );
  };

  return (
    <>
      {/* LuxPlus rows get wider From/To fields (see ScheduleTableRow) — "md" clips them. */}
      <Dialog fullWidth maxWidth={displayRow.isLuxPlus ? 'lg' : 'md'} open={isOpen} onClose={onClose}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {displayRow.brand} schedules
          <IconButton aria-label="Close" onClick={onClose} size="small">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {bulkUpdateError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {bulkUpdateError}
            </Alert>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" spacing={2} sx={{ pt: 2, mb: 3 }}>
            <NewScheduleFormBase
              schedule={{ start: bulkStart, end: bulkEnd }}
              startUpdated={!!bulkStart}
              endUpdated={!!bulkEnd}
              updateStart={(value) => setBulkStart(value ?? '')}
              updateEnd={(value) => setBulkEnd(value ?? '')}
              onReset={onBulkReset}
              disabled={isTableSaving || isDeletingAll}
            />
            <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_HOTEL_COORDINATOR]}>
              <Button
                variant="contained"
                disabled={!bulkStart || !bulkEnd || editableSchedules.length === 0 || isTableSaving || isDeletingAll}
                onClick={onBulkApply}
              >
                Bulk Update
              </Button>
            </PermissionedComponent>
          </Stack>
          <ScheduleTable
            ref={scheduleTableRef}
            brand={brand}
            offerId={offerId}
            schedules={displayRow.schedules}
            types={types}
            shouldMigrateSchedule={false}
            onSavingChange={setIsTableSaving}
            isDeletingAll={isDeletingAll}
            onSaveFinished={onSaveFinished}
            onDelete={onDelete}
            onUpdateErrors={onUpdateErrors}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', px: 3, pb: 2 }}>
          <PermissionedComponent>
            {isDeleteAllUndoWindowOpen ? (
              <Stack alignItems="center" direction="row" spacing={1}>
                <Typography color="text.secondary" variant="body2">
                  Deleting…
                </Typography>
                <Button size="small" onClick={cancelDeleteAll}>
                  Undo
                </Button>
              </Stack>
            ) : (
              <Button
                color="error"
                disabled={editableSchedules.length === 0 || isDeletingAll}
                variant="outlined"
                onClick={() => setIsDeleteAllOpen(true)}
              >
                {isDeletingAll ? 'Deleting…' : 'Delete All'}
              </Button>
            )}
          </PermissionedComponent>
        </DialogActions>
      </Dialog>
      <Dialog open={isDeleteAllOpen} onClose={() => setIsDeleteAllOpen(false)}>
        <DialogTitle>Delete all schedules for {brandLabel}?</DialogTitle>
        <DialogContent>
          <Stack spacing={1}>
            <Typography>
              This will delete all {editableSchedules.length} {pluralize('schedule', editableSchedules.length)} for{' '}
              {brandLabel}. You will have 5 seconds to undo.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsDeleteAllOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDeleteAll} autoFocus>
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
